import React from 'react';
import styled, { css } from 'styled-components';

import { CommonTheme } from 'app/shared/theme';
import { useLinkClickBehavior } from 'app/shared/utils/useLinkClickBehavior';
import {
  withButtonStyleCSS,
  WithButtonStyleProps,
  withCursorCSS,
  WithCursorProps,
  withDimensionsCSS,
  WithDimensionsProps,
  withSpacingCSS,
  WithSpacingProps,
} from 'app/shared/components/styleUtils';
import {
  Icon,
  NavigationLinkProps,
  ThemeWrapper,
} from 'app/shared/components/atoms';

export interface NavigationButtonProps
  extends NavigationLinkProps,
    WithSpacingProps,
    WithDimensionsProps,
    WithCursorProps,
    WithButtonStyleProps {
  trailingIcon?: React.FC<
    React.PropsWithChildren<React.SVGProps<SVGSVGElement>>
  >;
  leadingIcon?: React.FC<
    React.PropsWithChildren<React.SVGProps<SVGSVGElement>>
  >;
  flex?: React.CSSProperties['flex'];
  textWrap?: React.CSSProperties['textWrap'];
  'data-qaid': string;
}

interface NavigationButtonCSSProps extends NavigationButtonProps {
  theme: CommonTheme;
}

const StyledLink = styled.a<NavigationButtonCSSProps>`
  ${withCursorCSS};
  ${withButtonStyleCSS};
  ${withSpacingCSS};
  ${withDimensionsCSS};
  flex-direction: row;

  ${({ flex, textWrap }) => css`
    ${flex && `flex: ${flex};`}
    ${textWrap && `text-wrap: ${textWrap};`}
  `}
`;

export const NavigationButton = ({
  children,
  openInNewTab,
  href,
  onClick,
  target,
  rel,
  trailingIcon,
  leadingIcon,
  cursor = 'pointer',
  ...props
}: NavigationButtonProps) => {
  const { anchorRef, handleClick, newRel, newTarget } = useLinkClickBehavior({
    openInNewTab,
    href,
    onClick,
    target,
    rel,
  });

  return (
    <StyledLink
      ref={anchorRef}
      onClick={handleClick}
      rel={newRel}
      target={newTarget}
      href={href}
      cursor={cursor}
      {...props}
    >
      {leadingIcon && <Icon size="sm" icon={leadingIcon} />}
      {children}
      {trailingIcon && <Icon size="md" icon={trailingIcon} />}
    </StyledLink>
  );
};

export const NavigationButtonWithCommonTheme = (
  props: NavigationButtonProps
) => (
  <ThemeWrapper themeName="common">
    <NavigationButton {...props} />
  </ThemeWrapper>
);
